import { type Colors } from '@factorialco/deprecated-design-system'
import { ShiftManagementEnumsShiftCreationModeEnum } from 'generated/resources/datatable.graphql'
import {
  ShiftManagementEnumsShiftStateEnum,
  ShiftsShiftFragment,
} from 'generated/resources/graphql'
import { computed, makeObservable } from 'mobx'
import moment from 'moment'
import momentTimezone from 'moment-timezone'

import { dateToString, stringToDate, timeToStringWithTimezone } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
// TODO: migrate to grapqhql
import { employeeUser } from 'modules/core/store/collections/users'

import employees from 'modules/employees/store/collections/employees'
import type Employee from 'modules/employees/store/models/Employee'
import locations, { employeeLocation } from 'modules/locations/store/collections/locations'
import workAreas from 'modules/locations/store/collections/workAreas'
import type Location from 'modules/locations/store/models/Location'
import type WorkArea from 'modules/locations/store/models/WorkArea'
import { ADD_SHIFT_OPTIONS, AddShiftOptionType } from 'modules/shift_management/lib/addShiftOptions'
import { nameFromHexCode } from 'modules/shift_management/lib/colors'
import { momentToDuration } from 'modules/shift_management/lib/hoursToDuration'
import { totalDurationFromUnpaidBreaks } from 'modules/shift_management/lib/totalDurationFromUnpaidBreaks'
import SwapShifts from 'modules/shift_management/store/internal/collections/SwapShifts'
import type View from 'modules/shift_management/store/internal/models/View'
import PlannedBreaks from 'modules/time_planning/store/internal/collections/plannedBreaks'
import type PlannedBreak from 'modules/time_planning/store/internal/models/PlannedBreak'

export const NO_WORK_AREA = 'none'
export const CREATION_MODE = {
  manual: 'manual',
  automatic: 'automatic',
} as const satisfies Record<string, ShiftManagementEnumsShiftCreationModeEnum>

export interface Props {
  id: number
  employeeId: number
  name: string | null
  startAt: moment.Moment
  endAt: moment.Moment
  state: ShiftManagementEnumsShiftStateEnum
  locationId?: number | null
  workAreaId?: number | null
  notes?: string | null
  extraHours?: boolean
  isOvernightByCalendarDay?: boolean
  color?: Colors | null
  defaultShiftId?: number | null
  defaultShiftTitle?: string | null
  templateWeekId?: number | null
  unassignedShiftId?: number | null
  preview?: boolean
  templateName?: string
  // TODO: Make this required once 'dev_shift_management_graphql_migration' is done
  plannedBreaks?: PlannedBreaks
  swapShifts?: SwapShifts
  swapShiftsRequests?: SwapShifts
  fragment?: ShiftsShiftFragment
  creationMode: ShiftManagementEnumsShiftCreationModeEnum
}

export interface ShiftPayload {
  id: number
  employee_id: number
  name: string | null
  start_at: string
  end_at: string
  conflict: boolean
  state: ShiftManagementEnumsShiftStateEnum
  location_id: number | null
  work_area_id: number | null
  notes: string | null
  extra_hours: boolean
  is_overnight_by_calendar_day: boolean | undefined
  color: string | null
  default_shift_id: number | null
  default_shift_title: string | null
  template_week_id: number | null
  unassigned_shift_id: number | null
  creation_mode: ShiftManagementEnumsShiftCreationModeEnum
}

class Shift {
  id: number
  employeeId: number
  name: string | null
  _startAt: moment.Moment
  _endAt: moment.Moment
  state: ShiftManagementEnumsShiftStateEnum
  locationId: number | null
  workAreaId: number | null
  notes: string | null
  extraHours: boolean
  isOvernightByCalendarDay: boolean
  color: Colors | null
  defaultShiftId: number | null
  defaultShiftTitle: string | null
  templateWeekId: number | null
  unassignedShiftId: number | null
  preview: boolean
  // TODO: Make this required once 'dev_shift_management_graphql_migration' is done
  templateName?: string
  plannedBreaks?: PlannedBreaks
  swapShifts?: SwapShifts
  swapShiftsRequests?: SwapShifts
  fragment?: ShiftsShiftFragment
  creationMode?: ShiftManagementEnumsShiftCreationModeEnum

  constructor(props: Props) {
    this.id = props.id
    this.employeeId = props.employeeId
    this.name = props.name
    this._startAt = props.startAt.clone()
    this._endAt = props.endAt.clone()
    this.state = props.state
    this.locationId = props.locationId || null
    this.workAreaId = props.workAreaId || null
    this.notes = props.notes || null
    this.extraHours = !!props.extraHours
    this.isOvernightByCalendarDay = !!props.isOvernightByCalendarDay
    this.color = props.color || null
    this.defaultShiftId = props.defaultShiftId || null
    this.defaultShiftTitle = props.defaultShiftTitle || null
    this.templateWeekId = props.templateWeekId || null
    this.unassignedShiftId = props.unassignedShiftId || null
    this.preview = props.preview || false

    this.templateName = props.templateName
    this.plannedBreaks = props.plannedBreaks
    this.swapShifts = props.swapShifts
    this.swapShiftsRequests = props.swapShiftsRequests
    this.fragment = props.fragment
    this.creationMode = props.creationMode

    makeObservable(this, {
      displayName: computed,
      employee: computed,
      employeeFullName: computed,
      employeeTimeZone: computed,
      startTime: computed,
      endTime: computed,
      startAtWithTZ: computed,
      endAtWithTZ: computed,
      currentStartDate: computed,
      currentEndDate: computed,
      duration: computed,
      weekNumber: computed,
      date: computed,
      isSingleDay: computed,
      isNightShift: computed,
      beginningReference: computed,
      endingReference: computed,
      employeeLocationId: computed,
      workAreaReference: computed,
      isOvertime: computed,
      isAnHourOrLessLong: computed,
      isDefault: computed,
      isFromTemplate: computed,
      isFromUnassigned: computed,
      typeForEvents: computed,
      isAutomatic: computed,
    })
  }

  /**
   * @deprecated Before using this method, consider using the `buildFromGraphQL` hook since
   * we are trying to move away from using mobx-rest and use GraphQL instead.
   */
  static buildFromRequest(props: ShiftPayload): Shift {
    const color = props.color ? nameFromHexCode(props.color) : null

    return new Shift({
      id: props.id,
      employeeId: props.employee_id,
      name: props.name,
      startAt: stringToDate(props.start_at, 'backend_datetime'),
      endAt: stringToDate(props.end_at, 'backend_datetime'),
      state: props.state,
      locationId: props.location_id,
      workAreaId: props.work_area_id,
      notes: props.notes,
      extraHours: props.extra_hours,
      isOvernightByCalendarDay: props.is_overnight_by_calendar_day,
      color,
      defaultShiftId: props.default_shift_id,
      defaultShiftTitle: props.default_shift_title,
      templateWeekId: props.template_week_id,
      unassignedShiftId: props.unassigned_shift_id,
      creationMode: props.creation_mode,
    })
  }

  static buildFromGraphQL(fragment: ShiftsShiftFragment): Shift {
    const color = fragment.color ? nameFromHexCode(fragment.color) : null

    return new Shift({
      id: fragment.id,
      employeeId: fragment.employeeId,
      name: fragment.name || null,
      startAt: stringToDate(fragment.startAt, 'backend_datetime'),
      endAt: stringToDate(fragment.endAt, 'backend_datetime'),
      state: fragment.state,
      locationId: fragment.locationId,
      workAreaId: fragment.locationsWorkAreaId,
      notes: fragment.notes,
      extraHours: !!fragment.extraHours,
      isOvernightByCalendarDay: !!fragment.isOvernightByCalendarDay,
      color,
      defaultShiftId: fragment.defaultShiftId,
      defaultShiftTitle: fragment.defaultShiftTitle,
      plannedBreaks: PlannedBreaks.buildFromRequest(fragment.plannedBreaks),
      swapShifts: SwapShifts.buildFromRequest(fragment.swapShifts),
      swapShiftsRequests: SwapShifts.buildFromRequest(fragment.swapShiftsRequests),
      templateName: this.templateName(fragment),
      unassignedShiftId: fragment.unassignedShiftId,
      fragment: fragment,
      creationMode: fragment.creationMode,
    })
  }

  static templateName(fragment: ShiftsShiftFragment): string {
    const { shiftConfiguration } = fragment
    const templateWeek = shiftConfiguration?.templateWeek
    const template = templateWeek?.template

    return templateWeek ? [template?.name, templateWeek.name].join(' - ') : ''
  }

  get displayName(): string {
    return this.name ?? this.templateName ?? ''
  }

  get employee(): Employee | undefined {
    return employees.find({ id: this.employeeId })
  }

  get employeeFullName(): string | undefined {
    return this.employee && employeeUser(this.employee)?.fullName()
  }

  get employeeTimeZone(): string | null | undefined {
    return this.employee ? employeeLocation(this.employee.attributes.location_id)?.timezone : null
  }

  // eslint-disable-next-line factorial/no-const-collections
  get employeeLocationId(): number | null | undefined {
    return this.employee ? this.employee.attributes.location_id : null
  }

  get location(): Location | undefined {
    return locations.find({
      id: this.locationId,
    })
  }

  get workArea(): WorkArea | undefined {
    return workAreas.find({
      id: this.workAreaId,
    })
  }

  get startTime(): string {
    return timeToStringWithTimezone(this._startAt, this.employeeTimeZone ?? '')
  }

  get endTime(): string {
    return timeToStringWithTimezone(this._endAt, this.employeeTimeZone ?? '')
  }

  get startAtWithTZ(): momentTimezone.Moment {
    return momentTimezone.tz(this._startAt, this.employeeTimeZone ?? '')
  }

  get endAtWithTZ(): momentTimezone.Moment {
    return momentTimezone.tz(this._endAt, this.employeeTimeZone ?? '')
  }

  get currentStartDate(): moment.Moment {
    return !this.employeeTimeZone ? this._startAt : this.startAtWithTZ
  }

  get currentEndDate(): moment.Moment {
    return !this.employeeTimeZone ? this._endAt : this.endAtWithTZ
  }

  get duration(): moment.Duration {
    return moment.duration(this.currentEndDate.diff(this.currentStartDate))
  }

  get durationUntilMidnight(): moment.Duration {
    const midnight = this.currentEndDate.clone().startOf('day')

    return moment.duration(midnight.diff(this.currentStartDate))
  }

  get durationFromMidnight(): moment.Duration {
    const midnight = this.currentEndDate.clone().startOf('day')

    return moment.duration(this.currentEndDate.diff(midnight))
  }

  get weekNumber(): number {
    return this.currentStartDate.week()
  }

  get date(): moment.Moment {
    return this.currentStartDate
  }

  get isDraft(): boolean {
    return this.state === 'draft'
  }

  get isPublished(): boolean {
    return this.state === 'published'
  }

  get isBackup(): boolean {
    return this.state === 'backup'
  }

  get isOvertime(): boolean {
    return !!this.extraHours
  }

  get isSingleDay(): boolean {
    return this.currentStartDate.isSame(this.currentEndDate, 'day')
  }

  get isNightShift(): boolean {
    return !this.currentStartDate.isSame(this.currentEndDate, 'day')
  }

  get beginningReference(): string {
    return this.isNightShift ? `${this.id}-beginning` : String(this.id)
  }

  get endingReference(): string {
    return this.isNightShift ? `${this.id}-ending` : String(this.id)
  }

  get workAreaReference(): string {
    const locationId = this.locationId || this.employeeLocationId
    const workAreaId = this.workAreaId || NO_WORK_AREA
    return `${locationId}-${workAreaId}`
  }

  get isAnHourOrLessLong(): boolean {
    return this.duration.as('hours') <= 1
  }

  get isDefault(): boolean {
    return !!this.defaultShiftId
  }

  get isFromTemplate(): boolean {
    return !!this.templateWeekId || !!this.templateName
  }

  get isFromUnassigned(): boolean {
    return !!this.unassignedShiftId
  }

  get typeForEvents(): AddShiftOptionType | null {
    if (this.isDefault) {
      return ADD_SHIFT_OPTIONS.defaultShift
    } else if (this.isFromTemplate) {
      return ADD_SHIFT_OPTIONS.template
    } else {
      return null
    }
  }

  get isAutomatic(): boolean {
    return this.creationMode === 'automatic'
  }

  durationWithBreaks(plannedBreaks: PlannedBreak[]): moment.Duration {
    if (plannedBreaks.length === 0) return this.duration
    return this.duration.clone().subtract(totalDurationFromUnpaidBreaks(plannedBreaks), 'seconds')
  }

  formattedDuration(plannedBreaks: PlannedBreak[]): string {
    const duration = this.durationWithBreaks(plannedBreaks)
    const { hours, minutes } = momentToDuration(duration)

    return i18n.t('shift_management.hours_information.new_duration', {
      hours,
      minutes: minutes || '', // To hide the 0 minutes
    })
  }

  isDate(date: moment.Moment): boolean {
    if (this.isOvernightByCalendarDay) {
      return this.isSameStartDay(date) || this.isSameEndDay(date)
    }

    return this.isSameStartDay(date)
  }

  // Returns if the shift belongs to a day and a hour for the daily view
  isHour(date: moment.Moment): boolean {
    return this.currentStartDate.format('DD/MM/YYYY HH') === date.format('DD/MM/YYYY HH')
  }

  isWeek(week: number): boolean {
    return this.weekNumber === week
  }

  // isBetween was not working properly so we are formatting the dates to check the
  // range instead. It is important to keep in mind the order of the formatting and
  // the granularity of the HH:mm so that the comparison works.
  inRange(firstDay: moment.Moment, lastDay: moment.Moment): boolean {
    const format = 'YYYY-MM-DD HH:mm'
    const formattedCurrentStartDate = this.currentStartDate.format(format)
    const formattedFirstDay = firstDay.format(format)
    const formattedLastDay = lastDay.format(format)

    return (
      formattedFirstDay <= formattedCurrentStartDate && formattedCurrentStartDate < formattedLastDay
    )
  }

  // isSameStartDay or isSameEndDay were not returning accurate checks for
  // timezone changes, especially with big differences, for example:
  // Admin in Barcelona (+2), Employee is Pacific (-11).
  // Refs: https://github.com/moment/moment/issues/3957 &
  // https://momentjscom.readthedocs.io/en/latest/moment/05-query/02-is-same/
  isSameStartDay(date: moment.Moment): boolean {
    return dateToString(this.currentStartDate, 'backend') === dateToString(date, 'backend')
  }

  isSameEndDay(date: moment.Moment): boolean {
    return dateToString(this.currentEndDate, 'backend') === dateToString(date, 'backend')
  }

  isBeginNightShift(date: moment.Moment): boolean {
    return this.isNightShift && this.isSameStartDay(date)
  }

  isEndNightShift(date: moment.Moment): boolean {
    return this.isNightShift && this.isSameEndDay(date)
  }

  isEndNightShiftExactMidnight = (date: moment.Moment): boolean => {
    return (
      this.isEndNightShift(date) &&
      this.currentEndDate.format('HH:mm') == date.clone().startOf('day').format('HH:mm')
    )
  }

  referenceKey(date: moment.Moment): string {
    if (this.isBeginNightShift(date)) {
      return this.beginningReference
    } else if (this.isEndNightShift(date)) {
      return this.endingReference
    } else {
      return String(this.id)
    }
  }

  isStartingReference(key: string): boolean {
    return this.isNightShift && this.beginningReference === key
  }

  isEndingReference(key: string): boolean {
    return this.isNightShift && this.endingReference === key
  }

  dateByReference(key: string): moment.Moment {
    return this.isEndingReference(key) ? this.date.clone().add(1, 'day').startOf('day') : this.date
  }

  hoursByReference(key: string): moment.Moment[] {
    const startAt = this.isEndingReference(key)
      ? this.date.clone().add(1, 'day').startOf('day')
      : this.currentStartDate
    const endAt = this.isStartingReference(key)
      ? this.date.clone().endOf('day')
      : this.currentEndDate
    const hours: moment.Moment[] = []
    let hour = startAt

    // Only less than, and not equal to keep the endAt out.
    // Ex. 8:00 - 10:00 => 8:00, 9:00 but 10:00 is out of the range
    while (hour < endAt) {
      hours.push(hour)
      hour = hour.clone().add(1, 'h')
    }

    return hours
  }

  datesByReference(key: string, view: View): moment.Moment[] {
    if (view.isDaily) return this.hoursByReference(key)

    return [this.dateByReference(key)]
  }

  rowKeyReference(view: View): string {
    if (view.isByAreas) {
      return this.workAreaReference
    }

    return this.employeeId.toString()
  }

  isDuring(date: moment.Moment): boolean {
    const format = 'YYYY-MM-DD HH'
    const formatDate = date.format(format)
    const startAt = this.currentStartDate.format(format)
    const endAt = this.currentEndDate.clone().subtract(1, 'minute').format(format)

    return startAt <= formatDate && formatDate <= endAt
  }

  // This method is used after we have already filtered the shifts
  // that are within the hour, hence the default value is 1 and not 0
  durationWithinHour(date: moment.Moment): number {
    const formatDate = date.clone().startOf('hour')
    const dateHours = formatDate.format('HH')
    const startHours = this.currentStartDate.format('HH')
    const startMinutes = this.currentStartDate.format('mm')
    const endHours = this.currentEndDate.format('HH')
    const endMinutes = this.currentEndDate.format('mm')

    // Use column 09h as reference
    // if start time matches column time
    // and end time also matches the column time eg: 09:15 - 09:45 (shorter than 1 hour shifts)
    // but are not full 24 hour shifts eg: 09:00 - 09:00 +1 (overnight)
    // return difference between end and start time
    // all other cases eg: 09:15 - 17:30 or 09:30 - 07:00 +1 (overnight but not 24hrs)
    // use leftOffset to calculate difference
    if (startHours == dateHours) {
      const duration = moment.duration(this.currentEndDate.diff(this.currentStartDate)).asHours()
      return endHours == dateHours && startMinutes != endMinutes
        ? duration
        : 1 - this.leftOffset(date)
    }

    // if start time does not match column time but end time does eg: 08:45 - 09:45
    // use rightOffset to calculate difference
    if (endHours == dateHours) {
      return 1 - this.rightOffset(date)
    }

    // start and end outside of the column eg: 08:45 - 10:15
    // return default 1 hour
    return 1
  }

  startHours(date: moment.Moment): number {
    return this.isEndNightShift(date) ? 0 : Number(this.currentStartDate.format('HH'))
  }

  endHours(date: moment.Moment): number {
    const value = Number(this.currentEndDate.clone().add(59, 'm').startOf('hour').format('HH'))

    return this.isBeginNightShift(date) || value === 0 ? value + 24 : value
  }

  // Offsets to adjust horizontal paddings in the daily view
  // and fractions of hours for employee count by hour
  leftOffset(date: moment.Moment): number {
    if (this.isEndNightShift(date)) return 0

    return Number(this.currentStartDate.format('mm')) / 60
  }

  rightOffset(date: moment.Moment): number {
    if (this.isBeginNightShift(date)) return 0

    const endMinutes = Number(this.currentEndDate.format('mm'))
    return endMinutes ? (60 - endMinutes) / 60 : 0
  }
}

export default Shift
